import React, { useState } from 'react';
import { HelpCircle } from 'lucide-react';
import HelpPanel from '../TrustPortal/HelpPanel';
import {
  AdminSecurityScreenId,
  getAdminSecurityHelpEntry,
} from '../../data/adminSecurityHelpText';

// ──────────────────────────────────────────────────────────────────────────
// AdminSecurityHelpButton — Admin → Security/Code screens' help trigger.
// ──────────────────────────────────────────────────────────────────────────
//
// Usage:
//   <AdminSecurityHelpButton screenId="compliance-drift" />
//
// Thin wrapper that:
//   1. Looks up the help entry from adminSecurityHelpText.ts by screenId
//   2. Owns the open/close state for the panel
//   3. Renders a (?) icon button + the shared HelpPanel drawer
//
// This mirrors the Trust Portal HelpIconButton pattern exactly, just with
// a different data source. The underlying HelpPanel component is shared
// between both domains — adding a new help-enabled section in the future
// is just a new data file + a new thin wrapper like this one.
//
// Internal navigation: if the entry carries `relatedLinks`, clicking a
// link dispatches a `jarvis-admin-navigate` window event which Admin.tsx
// listens for and uses to jump the user to the target screen.

interface AdminSecurityHelpButtonProps {
  screenId: AdminSecurityScreenId;
  // Match Trust Portal's HelpIconButton prop API so host screens can
  // swap between domains without changing their call site structure.
  size?: 'sm' | 'md';
  showLabel?: boolean;
  className?: string;
}

const AdminSecurityHelpButton: React.FC<AdminSecurityHelpButtonProps> = ({
  screenId,
  size = 'md',
  showLabel = false,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const iconSizeClass = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  const paddingClass = size === 'sm' ? 'p-1.5' : 'p-2';

  const entry = getAdminSecurityHelpEntry(screenId);

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className={`inline-flex items-center gap-1.5 ${paddingClass} rounded-full text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors border border-transparent hover:border-blue-200 ${className}`}
        title="Open help for this screen"
        aria-label="Open help for this screen"
      >
        <HelpCircle className={iconSizeClass} />
        {showLabel && (
          <span className="text-sm font-medium">Help</span>
        )}
      </button>

      <HelpPanel
        entry={entry}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
      />
    </>
  );
};

export default AdminSecurityHelpButton;
